import api from "@/services/api/apiClient";
import {
  AlertCircle,
  CheckCircle,
  ChevronRight,
  FileText,
  HeartPulse,
  Loader2,
  Plane,
  Plus,
  RefreshCw,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DemoNavigator } from "../../components/demo/DemoNavigator";
import { PageBanner } from "../../components/ui/PageBanner";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";
import { i18nToast } from "../../utils/i18nToast";

type RequestType = "repatriation" | "treatment" | "prescription" | "other";

interface InsuranceRequest {
  id: string;
  insurance_name: string;
  policy_number?: string;
  request_type: RequestType;
  description: string;
  status: "pending" | "submitted" | "approved" | "rejected";
  reference_nr?: string;
  created_at: string;
}

const TYPE_CONFIG: Record<
  RequestType,
  { labelKey: keyof Translation; icon: React.ElementType; color: string }
> = {
  repatriation: {
    labelKey: "insTypeRepatriation",
    icon: Plane,
    color: "text-red-600",
  },
  treatment: {
    labelKey: "insTypeTreatment",
    icon: HeartPulse,
    color: "text-blue-600",
  },
  prescription: {
    labelKey: "insTypePrescription",
    icon: FileText,
    color: "text-purple-600",
  },
  other: {
    labelKey: "insTypeOther",
    icon: FileText,
    color: "text-gray-600",
  },
};

const STATUS_CONFIG: Record<
  string,
  { labelKey: keyof Translation; bg: string; text: string }
> = {
  pending: {
    labelKey: "insStatusPending",
    bg: "bg-yellow-100",
    text: "text-yellow-800",
  },
  submitted: {
    labelKey: "insStatusSubmitted",
    bg: "bg-blue-100",
    text: "text-blue-800",
  },
  approved: {
    labelKey: "insStatusApproved",
    bg: "bg-green-100",
    text: "text-green-800",
  },
  rejected: {
    labelKey: "insStatusRejected",
    bg: "bg-red-100",
    text: "text-red-800",
  },
};

const InsuranceRequestPage: React.FC = () => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const navigate = useNavigate();
  const label = (key: keyof Translation) => String(t[key] ?? key);
  const [requests, setRequests] = useState<InsuranceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [insuranceName, setInsuranceName] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [requestType, setRequestType] = useState<RequestType>("repatriation");
  const [description, setDescription] = useState("");

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/insurance-requests");
      setRequests(res.data?.data ?? []);
    } catch {
      // no-op – show empty state
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!insuranceName.trim() || !description.trim()) {
      i18nToast.error(t.insRequiredError);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/insurance-requests", {
        insuranceName,
        policyNumber: policyNumber || undefined,
        requestType,
        description,
      });
      i18nToast.success(t.insSubmitted);
      setShowForm(false);
      setInsuranceName("");
      setPolicyNumber("");
      setDescription("");
      setRequestType("repatriation");
      loadRequests();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? t.insSubmitFailed;
      i18nToast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-24">
      <PageBanner
        icon={FileText}
        title={t.insTitle}
        description={t.insDescription}
        gradient="from-orange-600 to-red-700"
      />

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <nav className="-mb-px flex space-x-5 overflow-x-auto">
          <button
            onClick={() => navigate("/traveler/health")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.edit}
          </button>
          <button
            onClick={() => navigate("/traveler/health/emergency")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.apptTabEmergency}
          </button>
          <button
            onClick={() => navigate("/traveler/health/appointment")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.apptTabAppointments}
          </button>
          <button
            onClick={() => navigate("/traveler/health/checkin")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.apptTabCheckin}
          </button>
          <button className="border-b-2 border-orange-500 py-3 px-1 text-sm font-medium text-orange-600 whitespace-nowrap">
            {t.apptTabInsurance}
          </button>
        </nav>
      </div>

      {/* Header actions */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {t.insMyRequests}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={loadRequests}
            aria-label={t.insRefresh}
            title={t.insRefresh}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-semibold hover:bg-orange-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t.insNew}
          </button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-5 flex items-center gap-2">
            <Plus className="w-4 h-4 text-orange-600" />
            {t.insFormTitle}
          </h3>
          <form onSubmit={submit} className="space-y-4">
            <div>
              {/* Not a <label>: this names a group of buttons, not a control. */}
              <span
                id="request-type-label"
                className="text-sm font-medium text-gray-700 block mb-1"
              >
                {t.insRequestType} <span className="text-red-500">*</span>
              </span>
              <div
                role="group"
                aria-labelledby="request-type-label"
                className="grid grid-cols-2 gap-2"
              >
                {(
                  Object.entries(TYPE_CONFIG) as [
                    RequestType,
                    (typeof TYPE_CONFIG)[RequestType],
                  ][]
                ).map(([type, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setRequestType(type)}
                      className={`flex items-center gap-2 p-3 rounded-xl border text-sm font-medium transition-colors text-left ${requestType === type ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                    >
                      <Icon
                        className={`w-4 h-4 flex-shrink-0 ${requestType === type ? "text-orange-600" : cfg.color}`}
                      />
                      {label(cfg.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  {t.insCompany} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={insuranceName}
                  onChange={(e) => setInsuranceName(e.target.value)}
                  placeholder={t.insCompanyPlaceholder}
                  required
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  {t.insPolicyNumber}{" "}
                  <span className="text-gray-400 font-normal">
                    {t.apptOptionalHint}
                  </span>
                </label>
                <input
                  type="text"
                  value={policyNumber}
                  onChange={(e) => setPolicyNumber(e.target.value)}
                  placeholder="A-123456"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="insurance-description"
                className="text-sm font-medium text-gray-700 block mb-1"
              >
                {t.insDescriptionLabel} <span className="text-red-500">*</span>
              </label>
              <textarea
                id="insurance-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                required
                placeholder={t.insDescriptionPlaceholder}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">{t.insEmergencyNote}</p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
              >
                {t.cancel}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-5 py-2 bg-orange-600 text-white rounded-xl text-sm font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                {submitting ? t.insSubmitting : t.insSubmit}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : requests.length === 0 && !showForm ? (
        <div className="text-center py-16 bg-white border border-gray-100 rounded-2xl">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-medium text-gray-900 mb-1">{t.insEmpty}</h3>
          <p className="text-sm text-gray-500 mb-5">{t.insEmptyHint}</p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-xl text-sm font-semibold hover:bg-orange-700"
          >
            <Plus className="w-4 h-4" />
            {t.insNew}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const type = TYPE_CONFIG[req.request_type] ?? TYPE_CONFIG.other;
            const status = STATUS_CONFIG[req.status] ?? STATUS_CONFIG.pending;
            const TypeIcon = type.icon;
            return (
              <div
                key={req.id}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:border-orange-200 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-xl bg-gray-50`}>
                    <TypeIcon className={`w-5 h-5 ${type.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-gray-900">
                        {label(type.labelKey)}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${status?.bg ?? "bg-gray-100"} ${status?.text ?? "text-gray-600"}`}
                      >
                        {status ? label(status.labelKey) : req.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 font-medium">
                      {req.insurance_name}
                      {req.policy_number && (
                        <span className="text-gray-400 font-normal">
                          {" "}
                          · {req.policy_number}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {req.description}
                    </p>
                    {req.reference_nr && (
                      <div className="flex items-center gap-1 mt-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        <span className="text-xs text-green-700 font-medium">
                          {t.insReference.replace("{ref}", req.reference_nr)}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {fmt.date(req.created_at)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DemoNavigator currentStep={5} />
    </div>
  );
};

export default InsuranceRequestPage;
